'use client';

import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { Color } from '../../../styles/Color';
import { ButtonLabel } from '../button/Button';
import { Z_INDEX_BASE, Z_INDEX_HEADER_TEXT } from '../../../styles/zIndex';
import { ICON_GAP_TRANSITION } from '../../../styles/animation/iconGapTransition';
import { ContentHeaderContentWrap } from './Header.styles';
import { Topbar } from '../top-bar/Topbar';
import { RouterLink } from '../link/RouterLink';
import HeaderImage, { HeaderImageVariants } from './HeaderImage';
import {
	ArrowBackIcon,
	ContentWrap,
	Css,
	H1,
	ITheme,
	Responsive,
} from '../../../ui';
import defaultHeaderImage from '../../../assets/default-content-header-image.webp';
import { isStringTranslatable } from '../../../utils/isStringTranslatable';
import FormattedTextForTranslation from '../text/FormattedTextForTranslation';

export interface IContentPageHeaderProps {
	title?: string;
	backLink?: string;
	backLinkText?: string;
	siteSectionId: string;
	hideHeaderImage?: boolean;
	headerImageUrl?: string;
	imageAlt: string;
	headerImageVariant: HeaderImageVariants;
}

export default function ContentPageHeader({
	backLink,
	backLinkText,
	title,
	siteSectionId,
	hideHeaderImage,
	headerImageUrl,
	imageAlt,
	headerImageVariant,
}: IContentPageHeaderProps) {
	const isSmallScreen = Responsive.useMatchesBreakpoint('tablet');
	const image = (
		<HeaderImage
			src={headerImageUrl ?? defaultHeaderImage}
			imageAlt={imageAlt}
			variant={headerImageVariant}
		/>
	);

	const titleParts = (title || '').split(' ');

	return (
		<ContentHeaderContentWrap>
			<Topbar siteSectionId={siteSectionId} />
			<HeaderContent id="main" hideHeaderImage={hideHeaderImage}>
				<BackButtonAndPageTitle>
					{backLink && (
						<BackButton
							// 	eslint-disable-next-line @typescript-eslint/ban-ts-comment
							// @ts-ignore
							as={RouterLink}
							href={backLink}
							aria-label="Go back to previous page"
						>
							<ArrowBackIcon aria-hidden="true" />
							<ButtonLabel>{backLinkText}</ButtonLabel>
						</BackButton>
					)}
					<PageTitle styling="h3">
						{titleParts.map((part, index) => {
							const isTranslatable = isStringTranslatable(part);

							return (
								<FormattedTextForTranslation
									key={index} /* eslint-disable-line react/no-array-index-key */
									index={index}
									isTranslatable={isTranslatable}
									part={part}
									stringParts={titleParts}
								/>
							);
						})}
					</PageTitle>
				</BackButtonAndPageTitle>
				{!hideHeaderImage && !isSmallScreen && image}
			</HeaderContent>

			{!hideHeaderImage && isSmallScreen && image}
		</ContentHeaderContentWrap>
	);
}

const BackButtonAndPageTitle = styled.div`
	display: flex;
	flex-direction: column;
	align-self: center;
	width: 100%;

	margin-bottom: ${Css.pxToRem(24)};

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		margin-bottom: 0;
	}
`;

const HeaderContent = styled(ContentWrap)<{ hideHeaderImage?: boolean }>`
	display: flex;
	flex: 1;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	margin-top: ${Css.pxToRem(40)};
	gap: ${Css.pxToRem(84)};

	h3 {
		line-height: normal;
		${({ hideHeaderImage }) =>
			hideHeaderImage &&
			css`
				max-width: 70%;

				@media (${Responsive.getMediaQueryForBreakpoint('mobile')}) {
					max-width: 100%;
				}
			`};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
		margin-top: ${Css.pxToRem(40)};
		margin-bottom: ${Css.pxToRem(24)};

		h3 {
			font-size: ${Css.pxToRem(32)};
		}
	}
`;

const BackButton = styled.button<{}, ITheme>`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: ${(props) => props.theme.button?.iconSeparator};
	text-decoration: none;
	font-weight: 700;
	color: ${Color.OnBackground};
	margin-bottom: ${Css.pxToRem(10)};
	width: fit-content;

	&:hover {
		cursor: pointer;
		color: ${Color.OnBackground};

		svg {
			transform: translateX(-${(props) => props.theme.button?.iconSeparator});
		}
	}

	svg {
		path {
			fill: ${Color.OnBackground};
		}
		transition: ${`transform ${ICON_GAP_TRANSITION}`};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		z-index: ${Z_INDEX_BASE};
	}
`;

const PageTitle = styled(H1)`
	color: ${Color.OnBackground};
	z-index: ${Z_INDEX_HEADER_TEXT};
	line-height: normal;
`;
